import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend, Area, AreaChart } from 'recharts';
import { TrendingUp, Dumbbell, Moon, Apple, Target, Activity } from "lucide-react";
import { useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Analytics } from "@/lib/analytics";
import { mergeSleepHoursByDate } from "@/lib/formatHealthValue";
import { getLocalDayKey } from "@/lib/localDateRange";

interface CombinedProgressChartProps {
  workouts: any[];
  sleepLogs: any[];
  healthSleepData?: Array<{ date: string; hours: number }>;
  nutritionLogs: any[];
  goals: any[];
  nutritionGoals?: any;
  dailyNutritionData?: any[];
}

export const CombinedProgressChart = ({ 
  workouts, 
  sleepLogs,
  healthSleepData = [],
  nutritionLogs, 
  goals,
  nutritionGoals,
  dailyNutritionData = []
}: CombinedProgressChartProps) => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    Analytics.progressChartViewed('combined');
  }, []);
  
  const mergedSleepByDate = useMemo(
    () => mergeSleepHoursByDate(sleepLogs, healthSleepData),
    [sleepLogs, healthSleepData],
  );

  const progressData = useMemo(() => {
    // Generate data for the last 30 days
    const days = [];
    const today = new Date();
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setHours(0, 0, 0, 0);
      date.setDate(date.getDate() - i);
      // Local calendar day — never UTC ISO (was wiping nutrition for many timezones)
      const dateStr = getLocalDayKey(date);
      
      // Calculate exercise score (0-100) based on workout frequency
      const weekStart = new Date(date);
      weekStart.setDate(weekStart.getDate() - 6);
      const weekWorkouts = workouts.filter(w => {
        if (!w.completed_at) return false;
        const workoutDate = new Date(w.completed_at);
        return workoutDate >= weekStart && workoutDate <= date;
      }).length;
      const exerciseScore = Math.min(100, (weekWorkouts / 4) * 100); // Target: 4 workouts per week
      
      // Calculate strength score (0-100) based on recent progress
      const recentStrengthWorkouts = workouts.filter(w => {
        if (!w.completed_at) return false;
        const workoutDate = new Date(w.completed_at);
        const twoWeeksAgo = new Date(date);
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
        return workoutDate >= twoWeeksAgo && workoutDate <= date && w.workout_type === 'strength';
      });
      
      // Simple strength progression calculation
      let strengthScore = 50; // baseline
      if (recentStrengthWorkouts.length >= 2) {
        strengthScore = Math.min(100, 50 + (recentStrengthWorkouts.length * 10));
      }
      
      // Calculate sleep score (0-100) — manual logs + Apple Health merged per date
      const mergedSleep = mergedSleepByDate.get(dateStr);
      let sleepScore = 0;
      if (mergedSleep && mergedSleep.hours > 0) {
        const durationScore = Math.min(100, (mergedSleep.hours / 8) * 70); // Target: 8 hours
        const qualityScore = mergedSleep.quality_rating
          ? (mergedSleep.quality_rating / 5) * 30
          : 15;
        sleepScore = Math.round(durationScore + qualityScore);
      }
      
      // Nutrition score from meals/food_entries (dailyNutritionData) + nutrition_logs fallback
      const nutritionLog = nutritionLogs.find(n => n.log_date === dateStr);
      const dailyData = dailyNutritionData.find((d) => {
        if (d.date === dateStr) return true;
        if (typeof d.date === 'string' && d.date.startsWith(dateStr)) return true;
        return false;
      });
      
      let nutritionScore = 0;
      
      const proteinGoal = nutritionGoals?.daily_protein || 150;
      const calorieGoal = nutritionGoals?.daily_calories || 2000;
      
      const totalsProtein = Number(dailyData?.totals?.protein ?? 0);
      const totalsCalories = Number(dailyData?.totals?.calories ?? 0);
      const logProtein = Number(nutritionLog?.protein_grams ?? 0);
      const logCalories = Number(nutritionLog?.calories ?? 0);

      const proteinGrams = totalsProtein > 0 ? totalsProtein : logProtein;
      const calories = totalsCalories > 0 ? totalsCalories : logCalories;
      
      if (proteinGrams > 0 || calories > 0) {
        const proteinCompliance = proteinGoal > 0
          ? Math.min(100, (proteinGrams / proteinGoal) * 100)
          : 0;
        const calorieCompliance = calorieGoal > 0
          ? Math.min(100, (calories / calorieGoal) * 100)
          : 0;
        if (proteinGrams > 0 && calories > 0) {
          nutritionScore = Math.round(proteinCompliance * 0.5 + calorieCompliance * 0.5);
        } else if (proteinGrams > 0) {
          nutritionScore = Math.round(proteinCompliance);
        } else {
          nutritionScore = Math.round(calorieCompliance);
        }
      }
      
      // Calculate combined score (weighted average)
      const combinedScore = Math.round(
        (exerciseScore * 0.3) + 
        (strengthScore * 0.25) + 
        (sleepScore * 0.25) + 
        (nutritionScore * 0.2)
      );
      
      const clamp = (v: number) => Math.max(0, Math.min(100, Math.round(v)));
      days.push({
        date: date.getDate(),
        fullDate: dateStr,
        exercise: clamp(exerciseScore),
        strength: clamp(strengthScore),
        sleep: clamp(sleepScore),
        nutrition: clamp(nutritionScore),
        combined: clamp(combinedScore),
        formattedDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      });
    }
    
    return days;
  }, [workouts, mergedSleepByDate, nutritionLogs, goals, nutritionGoals, dailyNutritionData]);

  // Auto-scroll to the right (latest data) on mount and when data updates
  useEffect(() => {
    const scrollToEnd = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
      }
    };
    
    // Delay to ensure chart is fully rendered
    const timeoutId = setTimeout(scrollToEnd, 100);
    
    return () => clearTimeout(timeoutId);
  }, [progressData]);

  const currentScores = useMemo(() => {
    const latest = progressData[progressData.length - 1];
    return {
      exercise: latest?.exercise || 0,
      strength: latest?.strength || 0,
      sleep: latest?.sleep || 0,
      nutrition: latest?.nutrition || 0,
      combined: latest?.combined || 0
    };
  }, [progressData]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-sm mb-2">{label}</p>
          {payload.map((entry: any) => (
            <p key={entry.dataKey} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="overflow-hidden bg-gradient-card border-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-normal">
          <TrendingUp className="h-4 w-4 md:h-5 md:w-5 text-primary" />
          {t('components.charts.combinedProgress.title')}
        </CardTitle>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4 mt-4">
          <div className="flex items-center gap-1.5 md:gap-2">
            <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-blue-500 flex-shrink-0" />
            <div className="text-xs md:text-sm min-w-0">
              <p className="text-muted-foreground truncate">{t('components.charts.combinedProgress.exercise')}</p>
              <p className="font-semibold">{currentScores.exercise}%</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2">
            <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-green-500 flex-shrink-0" />
            <div className="text-xs md:text-sm min-w-0">
              <p className="text-muted-foreground truncate">{t('components.charts.combinedProgress.strength')}</p>
              <p className="font-semibold">{currentScores.strength}%</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2">
            <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-purple-500 flex-shrink-0" />
            <div className="text-xs md:text-sm min-w-0">
              <p className="text-muted-foreground truncate">{t('components.charts.combinedProgress.sleep')}</p>
              <p className="font-semibold">{currentScores.sleep}%</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2">
            <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-orange-500 flex-shrink-0" />
            <div className="text-xs md:text-sm min-w-0">
              <p className="text-muted-foreground truncate">{t('components.charts.combinedProgress.nutrition')}</p>
              <p className="font-semibold">{currentScores.nutrition}%</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2">
            <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-cyan-500 flex-shrink-0" />
            <div className="text-xs md:text-sm min-w-0">
              <p className="text-muted-foreground truncate">{t('components.charts.combinedProgress.combined')}</p>
              <p className="font-semibold">{currentScores.combined}%</p>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-2 md:px-6 overflow-hidden">
        {/* Mobile: Horizontal scroll container */}
        <div 
          ref={scrollContainerRef} 
          className="overflow-x-auto pb-4 scrollbar-integrated scroll-smooth"
        >
          <div className="min-w-[600px] md:min-w-0">
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={progressData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="exerciseGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="strengthGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="sleepGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="nutritionGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="combinedGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="formattedDate" 
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                  minTickGap={30}
                />
                <YAxis 
                  domain={[0, 100]}
                  tick={{ fontSize: 11 }}
                  width={35}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="exercise"
                  name={t('components.charts.combinedProgress.exercise')}
                  stroke="#3b82f6"
                  fill="url(#exerciseGradient)"
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                />
                <Area
                  type="monotone"
                  dataKey="strength"
                  name={t('components.charts.combinedProgress.strength')}
                  stroke="#10b981"
                  fill="url(#strengthGradient)"
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                />
                <Area
                  type="monotone"
                  dataKey="sleep"
                  name={t('components.charts.combinedProgress.sleep')}
                  stroke="#8b5cf6"
                  fill="url(#sleepGradient)"
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                />
                <Area
                  type="monotone"
                  dataKey="nutrition"
                  name={t('components.charts.combinedProgress.nutrition')}
                  stroke="#f97316"
                  fill="url(#nutritionGradient)"
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                />
                <Area
                  type="monotone"
                  dataKey="combined"
                  name={t('components.charts.combinedProgress.combined')}
                  stroke="#06b6d4"
                  fill="url(#combinedGradient)"
                  strokeWidth={4.5}
                  dot={false}
                  activeDot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Legend with icons */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 px-2">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <span className="text-xs md:text-sm text-muted-foreground">{t('components.charts.combinedProgress.exercise')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Dumbbell className="h-4 w-4 text-green-500 flex-shrink-0" />
            <span className="text-xs md:text-sm text-muted-foreground">{t('components.charts.combinedProgress.strength')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Moon className="h-4 w-4 text-purple-500 flex-shrink-0" />
            <span className="text-xs md:text-sm text-muted-foreground">{t('components.charts.combinedProgress.sleep')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Apple className="h-4 w-4 text-orange-500 flex-shrink-0" />
            <span className="text-xs md:text-sm text-muted-foreground">{t('components.charts.combinedProgress.nutrition')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-cyan-500 flex-shrink-0" />
            <span className="text-xs md:text-sm text-muted-foreground font-bold">{t('components.charts.combinedProgress.combined')}</span>
          </div>
        </div>
        
        <div className="mt-4 text-xs md:text-sm text-muted-foreground space-y-1">
          <p>{t('components.charts.combinedProgress.footerExercise')}</p>
          <p>{t('components.charts.combinedProgress.footerStrength')}</p>
          <p>{t('components.charts.combinedProgress.footerSleep')}</p>
          <p>{t('components.charts.combinedProgress.footerNutrition')}</p>
          <p>{t('components.charts.combinedProgress.footerCombined')}</p>
        </div>
      </CardContent>
    </Card>
  );
};